/** Mission screen state for one device.
 *
 *  The mission-agent dashboard kept this in a zustand store fed by an SSE
 *  stream. Here the live signal is the existing device socket
 *  (ws/missionagent/<device>/, Odd's feed): any message for the mission being
 *  viewed triggers a debounced refetch of the read API, with a slow poll as a
 *  fallback because the engine commits a moment after the socket reports the
 *  raw device event.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCookies } from "react-cookie";

import useMissionAgentSocket from "../../hooks/useMissionAgentSocket";
import {
  errorMessage,
  fetchDashboard,
  fetchDeviceMissions,
  fetchUpdates,
} from "./api";
import type {
  Dashboard,
  FeedItem,
  MissionListRow,
} from "./types";

const POLL_MS = 10000;
const SOCKET_DEBOUNCE_MS = 2000;
/** A mission this page started (the simulator) is looked for this often, for this long, until listed. */
const NEW_MISSION_RETRY_MS = 2000;
const NEW_MISSION_TIMEOUT_MS = 30000;
export function useMission(deviceId: string | undefined) {
  const [cookies] = useCookies(["access_token"]);
  const token: string | undefined = cookies.access_token;
  const socket = useMissionAgentSocket(deviceId);

  const [missions, setMissions] = useState<MissionListRow[] | null>(null);
  const [missionId, setMissionId] = useState<string | null>(null);
  // Follow the device's current mission until the viewer picks another one.
  const [following, setFollowing] = useState(true);
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updates, setUpdates] = useState<FeedItem[]>([]);

  const missionRef = useRef<string | null>(null);
  missionRef.current = missionId;
  const followingRef = useRef(following);
  followingRef.current = following;
  // Missions Sentio recorded but the engine never processed have no engine
  // data to fetch (every read would 404); the page says so instead.
  const unprocessedRef = useRef<Set<string>>(new Set());
  unprocessedRef.current = new Set((missions ?? []).filter((m) => !m.in_engine).map((m) => m.mission_id));

  const loadMissions = useCallback(async (): Promise<MissionListRow[] | null> => {
    if (!token || !deviceId) return null;
    try {
      const list = await fetchDeviceMissions(token, deviceId);
      setMissions(list);
      if (followingRef.current || !missionRef.current) {
        setMissionId(list.find((m) => m.current)?.mission_id ?? null);
      }
      return list;
    } catch (err) {
      setMissions([]);
      setError(errorMessage(err));
      return null;
    }
  }, [token, deviceId]);

  // A mission this page started: the list is read until it has it (the first read can come a moment before
  // the mission is recorded), and the page follows it.
  const newMissionTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const followNewMission = useCallback(
    (interventionId: string) => {
      if (newMissionTimer.current) clearTimeout(newMissionTimer.current);
      setFollowing(true);
      followingRef.current = true;
      const giveUpAt = Date.now() + NEW_MISSION_TIMEOUT_MS;
      const attempt = async () => {
        newMissionTimer.current = null;
        const list = await loadMissions();
        const listed = (list ?? []).some((m) => m.intervention_id === interventionId);
        if (!listed && Date.now() < giveUpAt) {
          newMissionTimer.current = setTimeout(() => void attempt(), NEW_MISSION_RETRY_MS);
        }
      };
      void attempt();
    },
    [loadMissions],
  );
  useEffect(
    () => () => {
      if (newMissionTimer.current) clearTimeout(newMissionTimer.current);
    },
    [deviceId],
  );

  const refresh = useCallback(async () => {
    const target = missionRef.current;
    if (!token || !target || unprocessedRef.current.has(target)) return;
    try {
      const next = await fetchDashboard(token, target);
      if (missionRef.current !== target) return;
      setDashboard(next);
      setError(null);
    } catch (err) {
      if (missionRef.current === target) setError(errorMessage(err));
    }
    try {
      const feed = await fetchUpdates(token, target);
      if (missionRef.current === target) setUpdates(feed);
    } catch {
      // Keep the last feed; the next refresh retries.
    }
  }, [token]);

  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  // The device's missions, once per device/login.
  useEffect(() => {
    setMissions(null);
    setMissionId(null);
    setFollowing(true);
    void loadMissions();
  }, [loadMissions]);

  // A newly selected mission starts from a clean screen.
  useEffect(() => {
    setDashboard(null);
    setUpdates([]);
    if (!missionId) return undefined;
    setLoading(true);
    // Only the mission still on screen may clear the spinner.
    void refresh().finally(() => {
      if (missionRef.current === missionId) setLoading(false);
    });
    // refreshRef, not refresh: the poll must see the latest token.
    const timer = setInterval(() => void refreshRef.current(), POLL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missionId]);

  // Socket activity -> refetch. A reset, or an intervention this page has not
  // seen, means a new mission: reload the list (and follow it if following).
  const latestMessage = socket.messages[0];
  useEffect(() => {
    if (!latestMessage) return undefined;
    const known = (missions ?? []).some((m) => m.intervention_id === latestMessage.intervention_id);
    const newMission =
      latestMessage.type === "missionagent_reset" || (Boolean(latestMessage.intervention_id) && !known);
    const timer = setTimeout(() => {
      if (newMission) void loadMissions();
      else void refreshRef.current();
    }, SOCKET_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // Only a new message should schedule work, not every list refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestMessage]);

  const selected = useMemo(
    () => (missions ?? []).find((m) => m.mission_id === missionId) ?? null,
    [missions, missionId],
  );

  const liveStatus = useMemo(() => {
    const message = socket.messages.find(
      (m) =>
        m.event_type === "device_status_received" &&
        (!selected || !m.intervention_id || m.intervention_id === selected.intervention_id),
    );
    const data = (message?.data ?? {}) as { device_status?: string; streaming?: boolean };
    return { status: data.device_status ?? null, streaming: data.streaming ?? null };
  }, [socket.messages, selected]);

  return {
    missions,
    missionId,
    selected,
    following,
    selectMission: (next: string) => {
      const current = (missions ?? []).find((m) => m.current)?.mission_id;
      setFollowing(next === current);
      setMissionId(next);
    },
    dashboard,
    loading,
    error,
    live: socket.status === "connected",
    liveStatus,
    updates,
    /** A mission this page started itself (the simulator): follow it, reading the list until it is in it,
     *  without waiting for the socket to report it. */
    followNewMission,
    /** Re-read the selected mission now (its dashboard and updates), without waiting for the poll. */
    refresh: () => refreshRef.current(),
  };
}
